import { router } from "@inertiajs/react"
import { Loader2, Mic, Plus } from "lucide-react"
import { useEffect, useMemo, useState } from "react"
import { toast } from "sonner"
import { CharacterPortraitSquare } from "@/components/characters/character-portrait-square"
import { useInitialLoadReporter } from "@/components/shot-panel/use-initial-load-reporter"
import { ActionHintTooltip } from "@/components/ui/action-hint-tooltip"
import { LabeledSwitch } from "@/components/ui/labeled-switch"
import { Button, Input } from "@/components/ui/primitives"
import { useShotPanelWorkspace } from "@/hooks/use-shot-panel-workspace"
import { useTranslation } from "@/hooks/use-translation"
import { SketchDialog } from "@/components/sketch/sketch-dialog"
import { createCharacter } from "@/lib/characters-api"
import { AssetTaskSelect, selectedAssetTaskId, useAssetTaskCandidates } from "@/components/tasks/asset-task-pick-dialog"
import { characterListThumbnail } from "@/lib/character-thumbnail"
import { patchShotCharacters, type ShotCharacterAssignment } from "@/lib/inertia-actions"
import { effectiveVideoGenerationProvider } from "@/lib/provider-labels"
import {
  addShotVoiceReference,
  removeShotReferenceInput,
  type ShotReferenceInputRow,
} from "@/lib/shot-reference-inputs-api"
import { fetchShotWorkspaceDetail, fetchProjectCast, type ProjectCastRow } from "@/lib/workspace-api"
import { cn } from "@/lib/utils"
import { UI_CARD_SELECTED, UI_CHOICE_CONTROL, UI_CHOICE_CONTROL_SM, UI_NESTED_SELECTED } from "@/lib/ui-selection"

type ShotCharacterAssignmentRow = {
  id: number
  outfit_id: number | null
  include_description_in_prompt?: boolean
}

type ShotCharacterDraft = { outfitId: number | null; includeDescription: boolean }

// Seedance принимает максимум 3 reference_audio — столько же голосовых референсов на шот.
const MAX_VOICE_REFS = 3

type CastSnapshot = {
  assignments: Map<number, ShotCharacterDraft>
  noPeople: boolean
  voiceRefs: ShotReferenceInputRow[]
}

type Props = {
  projectId: number
  shotId: number
  onClose: () => void
  onDataChanged: () => void
  onInitialLoadSettled?: () => void
}

export function ShotCharactersView({ projectId, shotId, onClose, onDataChanged, onInitialLoadSettled }: Props) {
  const { t } = useTranslation(["shot", "common"])
  const taskCandidates = useAssetTaskCandidates(projectId, "character", true)
  const [createTaskId, setCreateTaskId] = useState<number | null>(null)
  const { project } = useShotPanelWorkspace()
  const voiceProviderSupported = effectiveVideoGenerationProvider(project ?? {}) === "byteplus_seedance_video"
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)

  const [projectCast, setProjectCast] = useState<ProjectCastRow[]>([])

  const [assignments, setAssignments] = useState<Map<number, ShotCharacterDraft>>(new Map())
  const [voiceRefs, setVoiceRefs] = useState<ShotReferenceInputRow[]>([])
  const [voiceToggling, setVoiceToggling] = useState<number | null>(null)
  const [noPeople, setNoPeople] = useState(false)
  const [filter, setFilter] = useState("")
  const [newName, setNewName] = useState("")
  const [creating, setCreating] = useState(false)
  const [createError, setCreateError] = useState<string | null>(null)
  const [preview, setPreview] = useState<{ url: string; title: string } | null>(null)
  const reportInitialLoad = useInitialLoadReporter(onInitialLoadSettled)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setLoadError(null)

    Promise.all([fetchShotWorkspaceDetail(projectId, shotId), fetchProjectCast(projectId)])
      .then(([detail, cast]) => {
        if (cancelled) return

        const { shot } = detail

        const chars: ShotCharacterAssignmentRow[] =
          ((shot as Record<string, unknown>).characters as ShotCharacterAssignmentRow[]) ?? []

        const noPeopleVal = Boolean((shot as Record<string, unknown>).storyboard_no_people)
        setNoPeople(noPeopleVal)

        const m = new Map<number, ShotCharacterDraft>()
        for (const a of chars) {
          m.set(a.id, {
            outfitId: a.outfit_id,
            includeDescription: a.include_description_in_prompt !== false,
          })
        }
        setAssignments(m)

        const refInputs = (shot as Record<string, unknown>).shot_reference_inputs as
          | { voice?: ShotReferenceInputRow[] }
          | undefined
        setVoiceRefs(refInputs?.voice ?? [])

        setProjectCast(cast)
        setFilter("")
        setNewName("")
        setCreateTaskId(null)
        setCreateError(null)
      })
      .catch((e) => {
        if (!cancelled) setLoadError(e instanceof Error ? e.message : String(e))
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false)
          reportInitialLoad()
        }
      })

    return () => {
      cancelled = true
    }
  }, [projectId, shotId, reportInitialLoad])

  const restoreCast = (snapshot: CastSnapshot) => {
    setAssignments(snapshot.assignments)
    setNoPeople(snapshot.noPeople)
    setVoiceRefs(snapshot.voiceRefs)
  }

  const persist = (nextAssignments: Map<number, ShotCharacterDraft>, nextNoPeople: boolean, snapshot: CastSnapshot) => {
    const body: ShotCharacterAssignment[] = Array.from(nextAssignments.entries()).map(([character_id, draft]) => ({
      character_id,
      outfit_id: draft.outfitId,
      include_description_in_prompt: draft.includeDescription,
    }))
    patchShotCharacters(
      projectId,
      shotId,
      { character_assignments: body, storyboard_no_people: nextNoPeople },
      {
        onSuccess: () => toast.success(t("common.saved")),
        onError: () => {
          toast.error(t("common.saveFailed"))
          restoreCast(snapshot)
        },
        onFinish: () => onDataChanged(),
      },
    )
  }

  const toggle = (id: number) => {
    const snapshot: CastSnapshot = { assignments, noPeople, voiceRefs }
    const next = new Map(assignments)
    if (next.has(id)) {
      next.delete(id)
      // Бэкенд удаляет голосовой референс откреплённого героя — синхронизируем локально.
      setVoiceRefs((prev) => prev.filter((r) => r.character_id !== id))
    } else {
      next.set(id, { outfitId: null, includeDescription: true })
    }
    setAssignments(next)
    persist(next, noPeople, snapshot)
  }

  const toggleVoice = async (row: ProjectCastRow) => {
    const existing = voiceRefs.find((r) => r.character_id === row.id)
    setVoiceToggling(row.id)
    try {
      if (existing) {
        await removeShotReferenceInput(projectId, shotId, existing.id)
        setVoiceRefs((prev) => prev.filter((r) => r.id !== existing.id))
      } else {
        const created = await addShotVoiceReference(projectId, shotId, row.id)
        setVoiceRefs((prev) => [...prev, created])
      }
      onDataChanged()
    } catch (e) {
      toast.error(e instanceof Error ? e.message : t("common.saveFailed"))
    } finally {
      setVoiceToggling(null)
    }
  }

  const changeOutfit = (id: number, outfitId: number | null) => {
    const current = assignments.get(id)
    if (!current) return
    const snapshot: CastSnapshot = { assignments, noPeople, voiceRefs }
    const next = new Map(assignments)
    next.set(id, { ...current, outfitId })
    setAssignments(next)
    persist(next, noPeople, snapshot)
  }

  const setIncludeDescription = (id: number, includeDescription: boolean) => {
    const current = assignments.get(id)
    if (!current) return
    const snapshot: CastSnapshot = { assignments, noPeople, voiceRefs }
    const next = new Map(assignments)
    next.set(id, { ...current, includeDescription })
    setAssignments(next)
    persist(next, noPeople, snapshot)
  }

  const toggleNoPeople = () => {
    const snapshot: CastSnapshot = { assignments, noPeople, voiceRefs }
    const next = !noPeople
    setNoPeople(next)
    persist(assignments, next, snapshot)
  }

  const handleCreate = async () => {
    const name = newName.trim()
    if (!name || taskCandidates.loading || taskCandidates.error) return
    setCreating(true)
    setCreateError(null)
    try {
      const snapshot: CastSnapshot = { assignments, noPeople, voiceRefs }
      const resp = await createCharacter(projectId, {
        name,
        task_id: selectedAssetTaskId(taskCandidates.tasks, createTaskId),
      })
      const characterId = resp.character_id
      const next = new Map(assignments)
      next.set(characterId, { outfitId: null, includeDescription: true })
      setAssignments(next)
      setNewName("")
      setCreateTaskId(null)
      const body: ShotCharacterAssignment[] = Array.from(next.entries()).map(([character_id, draft]) => ({
        character_id,
        outfit_id: draft.outfitId,
        include_description_in_prompt: draft.includeDescription,
      }))
      patchShotCharacters(
        projectId,
        shotId,
        { character_assignments: body, storyboard_no_people: noPeople },
        {
          onSuccess: () => {
            onClose()
            router.visit(`/projects/${projectId}/workspace/characters#character-${characterId}`)
          },
          onError: () => {
            toast.error(t("common.saveFailed"))
            restoreCast(snapshot)
            setCreating(false)
          },
          onFinish: () => onDataChanged(),
        },
      )
    } catch (e) {
      setCreateError(e instanceof Error ? e.message : String(e))
      setCreating(false)
    }
  }

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase()
    return q
      ? projectCast.filter((r) => r.name.toLowerCase().includes(q) || (r.role ?? "").toLowerCase().includes(q))
      : projectCast
  }, [filter, projectCast])

  if (loading) {
    return (
      <div className="flex flex-1 items-center justify-center p-10">
        <Loader2 className="h-6 w-6 animate-spin text-ui-text-muted" />
      </div>
    )
  }

  if (loadError) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-3 p-10 text-center">
        <p className="text-sm text-ui-status-failed">{loadError}</p>
        <Button type="button" size="sm" variant="ghost" onClick={onClose}>
          {t("common.close")}
        </Button>
      </div>
    )
  }

  const renderOutfitRadio = (opts: {
    name: string
    active: boolean
    label: string
    thumb: string | null | undefined
    full: string | null | undefined
    onSelect: () => void
  }) => (
    <label
      className={cn(
        "flex cursor-pointer items-center gap-2.5 rounded-lg px-2 py-1.5 transition-colors",
        opts.active
          ? UI_NESTED_SELECTED
          : "text-ui-text-secondary hover:bg-ui-bg-elevated/[0.04] hover:text-ui-text-primary",
      )}
    >
      <input
        type="radio"
        name={opts.name}
        checked={opts.active}
        onChange={opts.onSelect}
        className={UI_CHOICE_CONTROL_SM}
      />
      {opts.thumb ? (
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
            setPreview({ url: (opts.full ?? opts.thumb) as string, title: opts.label })
          }}
          className="shrink-0 cursor-zoom-in rounded-md focus-visible:ring-2 focus-visible:ring-[var(--ui-focus-ring)] focus-visible:outline-none"
          aria-label={t("characters.openImage", { name: opts.label })}
        >
          <img src={opts.thumb} alt="" className="h-8 w-8 rounded-md border border-ui-border object-cover" />
        </button>
      ) : (
        <div className="h-8 w-8 shrink-0 rounded-md border border-dashed border-ui-border-strong" />
      )}
      <span className="truncate text-xs font-medium">{opts.label}</span>
    </label>
  )

  return (
    <div className="flex max-h-full flex-col">
      <div className="shrink-0 px-4 pb-2">
        <h2 className="text-sm font-semibold text-ui-text-primary">{t("characters.title")}</h2>
        <p className="mt-0.5 text-xs text-ui-text-muted">{t("characters.hint")}</p>
      </div>
      <div className="shrink-0 border-b border-ui-border px-4 py-2.5">
        <Input
          placeholder={t("placeholders.searchNameRole")}
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
        />
      </div>

      <div className="it2tv-scrollbar min-h-0 flex-1 overflow-y-auto p-4">
        {filtered.length === 0 ? (
          <p className="mb-3 text-sm text-ui-text-muted">{t("characters.noMatching")}</p>
        ) : (
          <ul className="mb-3 space-y-2">
            {filtered.map((row) => {
              const draft = assignments.get(row.id)
              const on = draft != null
              const outfitId = draft?.outfitId ?? null
              const listThumb = characterListThumbnail(row)
              const previewUrl = row.portrait_url ?? row.reference_display_url ?? listThumb
              const portrait = (
                <CharacterPortraitSquare
                  name={row.name}
                  size="compact"
                  portrait_thumb_url={row.portrait_thumb_url}
                  portrait_url={row.portrait_url}
                  reference_sheet_url={row.reference_sheet_url}
                />
              )
              return (
                <li key={row.id}>
                  <div
                    className={cn(
                      "flex items-start gap-3 rounded-lg border px-3 py-2.5 text-sm transition-colors",
                      on
                        ? UI_CARD_SELECTED
                        : "border-ui-border bg-ui-surface-raised text-ui-text-secondary hover:border-ui-border-strong hover:bg-ui-bg-elevated/[0.05]",
                    )}
                  >
                    <label className="flex cursor-pointer items-center gap-3 pt-0.5">
                      <input
                        type="checkbox"
                        checked={on}
                        onChange={() => toggle(row.id)}
                        className={UI_CHOICE_CONTROL}
                      />
                    </label>
                    {previewUrl ? (
                      <button
                        type="button"
                        onClick={() =>
                          setPreview({
                            url: row.portrait_download_url ?? row.reference_download_url ?? previewUrl,
                            title: row.name,
                          })
                        }
                        className="shrink-0 cursor-zoom-in rounded-lg focus-visible:ring-2 focus-visible:ring-[var(--ui-focus-ring)] focus-visible:outline-none"
                        aria-label={t("characters.openImage", { name: row.name })}
                      >
                        {portrait}
                      </button>
                    ) : (
                      portrait
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <div className="truncate font-medium text-ui-text-primary">{row.name}</div>
                          {row.role && <div className="truncate text-[11px] text-ui-text-muted">{row.role}</div>}
                        </div>
                        {draft ? (
                          <LabeledSwitch
                            layout="inline"
                            className="shrink-0"
                            label={t("characters.includeDescription")}
                            checked={draft.includeDescription}
                            onCheckedChange={(checked) => setIncludeDescription(row.id, checked)}
                          />
                        ) : null}
                      </div>
                      {on &&
                        (() => {
                          const voiceRef = voiceRefs.find((r) => r.character_id === row.id)
                          const checked = voiceRef != null
                          const disabledReason = !voiceProviderSupported
                            ? t("characters.useVoiceProviderUnsupported")
                            : !row.voice_ready
                              ? t("characters.useVoiceNotReady")
                              : voiceRefs.length >= MAX_VOICE_REFS
                                ? t("characters.useVoiceLimit", { max: MAX_VOICE_REFS })
                                : undefined
                          // Снять уже включённый голос можно всегда; блокируем только включение.
                          const disabled = voiceToggling === row.id || (!checked && disabledReason != null)
                          return (
                            <div className="mt-2">
                              <ActionHintTooltip label={!checked ? disabledReason : undefined}>
                                <label
                                  className={cn(
                                    "flex items-center gap-2 text-xs",
                                    disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer",
                                  )}
                                >
                                  <input
                                    type="checkbox"
                                    checked={checked}
                                    disabled={disabled}
                                    onChange={() => void toggleVoice(row)}
                                    className={UI_CHOICE_CONTROL_SM}
                                  />
                                  <Mic className="h-3 w-3 shrink-0 text-ui-text-muted" />
                                  <span className="text-ui-text-secondary">{t("characters.useVoice")}</span>
                                  {checked && voiceRef ? (
                                    <span className="font-mono text-[10px] text-ui-accent-text">{voiceRef.label}</span>
                                  ) : null}
                                  {voiceToggling === row.id ? (
                                    <Loader2 className="h-3 w-3 animate-spin text-ui-text-muted" />
                                  ) : null}
                                </label>
                              </ActionHintTooltip>
                            </div>
                          )
                        })()}
                      {on && row.outfits.length > 0 && (
                        <div className="mt-2 space-y-1">
                          {renderOutfitRadio({
                            name: `char_outfit_${row.id}`,
                            active: outfitId == null,
                            label: t("characters.defaultOutfit"),
                            thumb: listThumb,
                            full:
                              row.portrait_download_url ??
                              row.reference_download_url ??
                              row.portrait_url ??
                              row.reference_display_url ??
                              listThumb,
                            onSelect: () => changeOutfit(row.id, null),
                          })}
                          {row.outfits.map((o) => (
                            <div key={o.id}>
                              {renderOutfitRadio({
                                name: `char_outfit_${row.id}`,
                                active: outfitId === o.id,
                                label: o.name,
                                thumb: o.reference_url,
                                full: o.reference_download_url ?? o.reference_full_url ?? o.reference_url,
                                onSelect: () => changeOutfit(row.id, o.id),
                              })}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </li>
              )
            })}
          </ul>
        )}
        <div className="rounded-lg border border-dashed border-white/[0.1] bg-ui-bg-elevated/[0.02] px-3 py-2.5">
          <div className="mb-1.5 text-xs font-semibold text-ui-text-secondary">{t("characters.quickAdd")}</div>
          <div className="flex flex-wrap items-center gap-2">
            <Input
              className="min-w-[180px] flex-1"
              placeholder={t("placeholders.newCharacterName")}
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault()
                  void handleCreate()
                }
              }}
            />
            <Button
              type="button"
              size="sm"
              variant="secondary"
              disabled={creating || taskCandidates.loading || Boolean(taskCandidates.error) || !newName.trim()}
              onClick={handleCreate}
            >
              {creating ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plus className="h-3 w-3" />}{" "}
              {t("characters.add")}
            </Button>
          </div>
          <AssetTaskSelect
            className="mt-2"
            tasks={taskCandidates.tasks}
            value={createTaskId}
            onChange={setCreateTaskId}
          />
          {createError || taskCandidates.error ? (
            <div className="mt-1 text-[11px] text-ui-status-failed">{createError || taskCandidates.error}</div>
          ) : null}
        </div>
      </div>

      <div className="shrink-0 border-t border-ui-border p-4">
        <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-white/[0.1] bg-ui-bg-elevated/[0.04] px-3 py-3">
          <input
            type="checkbox"
            checked={noPeople}
            onChange={toggleNoPeople}
            className={cn("mt-0.5", UI_CHOICE_CONTROL)}
          />
          <span>
            <span className="block text-sm font-medium text-ui-text-primary">{t("characters.noPeople")}</span>
            <span className="mt-0.5 block text-[11px] leading-snug text-ui-text-muted">
              {t("characters.noPeopleHint")}
            </span>
          </span>
        </label>
      </div>

      <SketchDialog
        open={!!preview}
        onOpenChange={(o) => !o && setPreview(null)}
        projectId={projectId}
        sourceUrl={preview?.url ?? null}
        presentation="view"
        allowEdit={false}
        persist="none"
        nested
        headerTitle={preview?.title}
      />
    </div>
  )
}
